import FixtureTeamComparison from "./FixtureTeamComparison";
import { cloneElement, useEffect, useMemo, useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  Alert,
  Avatar,
  Button,
  CircularProgress,
  TextField,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { FEATURES, hasFeatureAccess } from "../helpers/featureAccess";
import { getFixtureScore } from "../helpers/leagueMatchdayAnalysis";
import { asOracleFixture } from "../helpers/adminLeague";
import { fetchFromApi } from "../helpers/fetchFromApi";
import { tipsRequest } from "../helpers/tipsWorkflow";
import { fetchFixtureMarketOdds } from "./OddsTooltip";
import { getLeagueFairMarketValues } from "../helpers/leagueFairMarkets";
import { workspaceIndications } from "../helpers/workspaceIndications";
import { getGermanFixtureDate } from "../helpers/fixtureDate";
import {
  buildFixturePerformanceRows,
  FIXTURE_TEAM_FIELDS,
  WorkspaceTableAndRanking,
} from "./FixtureWorkspacePanel";
import { FixtureSquads } from "./FixtureSquads";
import {
  adaptiveObservations,
  adaptiveTeamValues,
  adaptiveLeagueContext,
  consolidatedRecentRows,
  leagueGoalContext,
  goalDifference,
  goalSum,
  numberOrNull,
  summarizeRecent,
} from "../helpers/experimentalFixture";
import "./ExperimentalFixtureWorkspace.css";

const SETTING_LABELS = {
  total_strength: "Spielstärke",
  total_goals: "Torschnitt",
  for_total: "Offensive gesamt",
  against_total: "Defensive gesamt",
  for_home: "Offensive Heim",
  against_home: "Defensive Heim",
  for_away: "Offensive Auswärts",
  against_away: "Defensive Auswärts",
  for_homefactor: "Offensiver Heimfaktor",
  against_homefactor: "Defensiver Heimfaktor",
  draw_factor: "Remisfaktor",
};
const EN_LABELS = {
  Spielstärke: "Team strength",
  Torschnitt: "Goals per game",
  "Offensive gesamt": "Goals for · overall",
  "Defensive gesamt": "Goals against · overall",
  "Offensive Heim": "Goals for · home",
  "Defensive Heim": "Goals against · home",
  "Offensive Auswärts": "Goals for · away",
  "Defensive Auswärts": "Goals against · away",
  "Offensiver Heimfaktor": "Attacking home factor",
  "Defensiver Heimfaktor": "Defensive home factor",
  Remisfaktor: "Draw factor",
  "Ergebnisbasiertes Modell · gespeichert": "Results-based model · stored",
  "Ergebnisbasiertes Modell · Vorschau": "Results-based model · preview",
  "Markt-Rückrechnung": "Market inversion",
  "Spielstärke & Torschnitt": "Team strength & goals per game",
  "Offensive & Defensive": "Attack & defence",
  "Heim- / Auswärtsdetails": "Home / away details",
  "Weitere Faktoren": "Other factors",
  "Spielstärke ist das Verhältnis Tore für / Tore gegen. Der Torschnitt ist deren Summe. Eine Änderung verteilt die Offensive und Defensive über die vorhandene betty-Rückrechnung.":
    "Strength is goals for divided by goals against; goals per game is their sum. Changes redistribute attack and defence through the existing betty calculation.",
  "Mittlere Tore für und gegen das Team; Änderungen werden auf Heim- und Auswärtswerte übertragen. Spielstärke und Torschnitt werden daraus abgeleitet.":
    "Average goals for and against the team. Changes update home and away values, from which strength and goals per game are derived.",
  "Basiswerte der jeweiligen Spielstätte. Die für diese Paarung relevante Seite ist hervorgehoben. Änderungen aktualisieren Gesamtwerte und Heimfaktoren.":
    "Venue-specific base values. The relevant venue is highlighted. Changes update overall values and home factors.",
  "Weitere vorhandene Modellfaktoren. Eine Vorschau zeigt ihre gemeinsame Wirkung; ohne explizites Speichern bleiben es lokale Entwürfe.":
    "Other existing model factors. Preview shows their combined effect; they remain local drafts until explicitly saved.",
  "Für dieses Spiel liegt keine gespeicherte Statistikprognose vor. Statistikmodell und Saisonstand im Statistikmodell-Bereich prüfen; historische Prognosen werden nicht aus heutigen Settings rekonstruiert.":
    "No stored statistical forecast for this match. Check the model and season state in the Statistics-based model area; historical forecasts are never reconstructed from current settings.",
  "Keine gespeicherte KI-Einordnung verfügbar. Die Datenvergleiche und deterministischen Erläuterungen bleiben nutzbar.":
    "No stored AI explanation available. Data comparisons and deterministic explanations remain usable.",
  "Die gespeicherte KI-Einordnung ist gegenüber der aktuellen Datengrundlage veraltet.":
    "The stored AI explanation is stale relative to the current inputs.",
  "Keine geprüfte gespeicherte KI-Einordnung verfügbar.":
    "No verified stored AI explanation available.",
  "Unzureichende Datenbasis": "Insufficient data",
  "Die Einordnung gilt nur für die damals übergebenen Daten.":
    "This explanation applies only to its original input data.",
  Mannschaftsrichtung: "Team direction",
  Torrichtung: "Goals direction",
  "Quelle im geladenen Record nicht angegeben":
    "Source not specified in the loaded record",
  "Zeitpunkt nicht angegeben": "Timestamp not specified",
};
function WorkspaceHelp({ children, text, target = "teamSettings" }) {
  const [open, setOpen] = useState(false);
  const { i18n } = useTranslation();
  return (
    <Tooltip
      open={open}
      onOpen={() => setOpen(true)}
      leaveDelay={180}
      onClose={() => setOpen(false)}
      title={
        <span>
          {text}{" "}
          <a href={"/help?target=" + target} target="_blank" rel="noreferrer">
            {i18n.language?.startsWith("en") ? "Learn more" : "Mehr erfahren"}
          </a>
        </span>
      }
    >
      <button
        type="button"
        className="ef-help"
        aria-expanded={open}
        onFocus={() => setOpen(true)}
        onBlur={(event) => {
          if (!event.relatedTarget?.closest(".MuiTooltip-popper"))
            setOpen(false);
        }}
        onMouseEnter={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
        }}
      >
        {children} ⓘ
      </button>
    </Tooltip>
  );
}
export function PreciseSettingInput({ value, label, onChange, disabled }) {
  const [draft, setDraft] = useState(null);
  const shown =
    numberOrNull(value) === null
      ? ""
      : Number(value).toLocaleString("de-DE", { maximumFractionDigits: 3 });
  return (
    <TextField
      size="small"
      label={label}
      value={draft ?? shown}
      disabled={disabled}
      inputProps={{ inputMode: "decimal" }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (
          draft !== null &&
          draft !== shown &&
          draft.trim() !== "" &&
          Number.isFinite(Number(draft.replace(",", ".")))
        )
          onChange(draft.replace(",", "."));
        setDraft(null);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.querySelector("input")?.blur();
        if (e.key === "Escape") setDraft(null);
      }}
    />
  );
}

export function ExperimentalFixtureWorkspace(props) {
  if (!hasFeatureAccess(FEATURES.EXPERIMENTAL_FIXTURE, props.user)) return null;
  return <MatchWorkspace key={props.row.id} {...props} />;
}
export function WorkspaceSection({ id, title, children, open = false }) {
  return (
    <details className="ef-section" id={"ef-" + id} open={open || undefined}>
      <summary>{title}</summary>
      <div className="ef-section-body">{children}</div>
    </details>
  );
}
function MatchWorkspace({
  row,
  homeDraft,
  awayDraft,
  fixtureDraft,
  calculation,
  performance = {},
  marketOdds,
  indicationThreshold,
  seasonParameters,
  strengthRanking,
  previousSeasonData,
  onClose,
  onTeamChange,
  onRecompile,
  onSaveTeams,
  onSaveFixtureEstimate,
  onReset,
  hasUnsavedTeams,
  hasCalculatedDraft,
  hasFixtureDraftChanges,
  fixtureEstimateSaved,
  actionError,
  formContent,
}) {
  const kickoff = asOracleFixture(row, row.league).kickoff;
  const original = useRef({
    home: { ...homeDraft },
    away: { ...awayDraft },
    goals: { home: fixtureDraft.homeGoalExp, away: fixtureDraft.awayGoalExp },
    probs: row.probs || {
      homewin: row.homewin,
      draw: row.draw,
      awaywin: row.awaywin,
    },
  });
  const { i18n } = useTranslation(),
    en = i18n.language?.startsWith("en"),
    t = (de, eng) => (en ? eng : de);
  const tr = (label) => (en ? EN_LABELS[label] || label : label);
  const metricHelp = (field) => {
    if (field === "total_strength")
      return t(
        "Spielstärke = mittlere Tore für / mittlere Tore gegen. Einheitenlos. Änderungen rechnen über die vorhandene betty-Logik auf die vier Heim-/Auswärtswerte zurück.",
        "Strength = mean goals for / mean goals against. Dimensionless. Editing uses the existing betty calculation to update the four venue values."
      );
    if (field === "total_goals")
      return t(
        "Torschnitt = mittlere Tore für + mittlere Tore gegen, in Toren pro Spiel. Änderungen erhalten die Spielstärke über die vorhandene Rückrechnung.",
        "Goals per game = mean goals for + mean goals against. Editing preserves strength through the existing calculation."
      );
    if (field === "draw_factor")
      return t(
        "Teambezogener Remisfaktor des ergebnisbasierten Modells, keine Wahrscheinlichkeit. Die Vorschau zeigt die gemeinsame Wirkung mit den Saisonparametern.",
        "Team draw factor of the results-based model, not a probability. Preview shows its joint effect with season parameters."
      );
    if (field.includes("factor"))
      return t(
        "Heimwert geteilt durch den Mittelwert aus Heim und Auswärts; einheitenlos. Eine Änderung verteilt die bestehenden Werte neu.",
        "Home value divided by the home/away mean; dimensionless. Editing redistributes the existing values."
      );
    return t(
      "Tore pro Spiel für bzw. gegen die Mannschaft, bezogen auf die beschriftete Spielstätte. Gesamtwerte und Faktoren hängen davon ab. Jede Spalte benennt ihre eigene Datenquelle; keine Wahrscheinlichkeit.",
      "Goals per match for or against the team at the labelled venue. Overall values and factors depend on these values. Each column has its own data source; not a probability."
    );
  };
  const fmt = (n, d = 3) =>
    numberOrNull(n) === null
      ? "—"
      : Number(n).toLocaleString(en ? "en-GB" : "de-DE", {
          minimumFractionDigits: 0,
          maximumFractionDigits: d,
        });
  const [fixtureContext, setFixtureContext] = useState(null);
  useEffect(() => {
    if (row.season?.name && row.round?.name) return;
    let active = true;
    fetchFromApi(`fetch-data-v3/fixtures/${row.id}?include=league;season;round`)
      .then((r) => {
        if (!r.ok) throw Error("FIXTURE_CONTEXT_UNAVAILABLE");
        return r.json();
      })
      .then((d) => {
        if (active) setFixtureContext(d.data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [row.id, row.season?.name, row.round?.name]);
  const [recentGames, setRecentGames] = useState(null),
    [workspace, setWorkspace] = useState(null),
    [loadError, setLoadError] = useState(""),
    [loading, setLoading] = useState(true),
    [market, setMarket] = useState(row.analysisContext?.market || "1x2"),
    [line, setLine] = useState(String(row.analysisContext?.line ?? "2.50")),
    [comparisonContext, setComparisonContext] = useState(""),
    [selection, setSelection] = useState(row.analysisContext?.selection || ""),
    [oddsResponse, setOddsResponse] = useState(null),
    [oddsError, setOddsError] = useState(""),
    [squads, setSquads] = useState(false),
    [busy, setBusy] = useState(false),
    [localError, setLocalError] = useState(""),
    [completedAction, setCompletedAction] = useState(""),
    [confirmClose, setConfirmClose] = useState(false);
  useEffect(() => {
    let active = true;
    setLoading(true);
    tipsRequest("statistic-model", {
      mode: "workspace",
      league_id: Number(row.league_id ?? row.league?.id),
      season_id: Number(row.season_id),
      fixture_id: Number(row.id),
    })
      .then((data) => {
        if (active) setWorkspace(data);
      })
      .catch((e) => {
        if (active) setLoadError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [row.id, row.league_id, row.league?.id, row.season_id]);
  useEffect(() => {
    let active = true;
    setOddsResponse(null);
    setOddsError("");
    fetchFixtureMarketOdds(row.id, market, { line })
      .then((data) => {
        if (active) setOddsResponse({key: `${row.id}:${market}:${line}`, data});
      })
      .catch((e) => {
        if (active) setOddsError(e.message);
      });
    return () => {
      active = false;
    };
  }, [row.id, market, line]);
  const odds = oddsResponse?.key === `${row.id}:${market}:${line}` ? oddsResponse.data : null;
  const teams = [
    { ...row.localTeam, ...homeDraft, side: "home" },
    { ...row.visitorTeam, ...awayDraft, side: "away" },
  ];
  useEffect(() => {
    if (
      !hasUnsavedTeams &&
      homeDraft?.for_home != null &&
      awayDraft?.for_home != null
    ) {
      original.current.home = { ...homeDraft };
      original.current.away = { ...awayDraft };
    }
  }, [homeDraft, awayDraft, hasUnsavedTeams]);
  const records = workspace?.records || [],
    selected = records.find(
      (r) => Number(r.pre?.fixture_id) === Number(row.id)
    ),
    comparison = workspace?.comparison?.rows?.find(
      (r) => Number(r.fixture_id) === Number(row.id)
    );
  const observations = useMemo(
    () => adaptiveObservations(workspace?.records, kickoff),
    [workspace, kickoff]
  );
  const resultGoals = odds?.prematch_context?.historical ? (odds.prematch_context.forecast?.goal_expectancies || {home:null,away:null}) : calculation?.detail?.goalexp ||
    calculation?.goalexp || {
      home: fixtureDraft.homeGoalExp,
      away: fixtureDraft.awayGoalExp,
    };
  const savedGoals =
    comparison?.result_model?.xg || row.goalexp || original.current.goals;
  const statForecast =
    workspace?.selected_fixture?.pre ||
    selected?.pre ||
    workspace?.selected_preview?.pre;
  const actualScore =
    getFixtureScore(row) || selected?.evaluation?.actual_goals;
  const statGoals = statForecast?.statistic_xg,
    derived = odds?.prematch_context?.market_derived || row.market_derived || row.market_derived_preview;
  const marketGoals = {
    home: derived?.market_expected_home_goals,
    away: derived?.market_expected_away_goals,
  };
  const storedProbs = row.probs || original.current.probs;
  const frozenForecast = odds?.prematch_context?.historical ? odds.prematch_context.forecast : null;
  const resultProbs = odds?.prematch_context?.historical ? (frozenForecast?.one_x_two || {}) : calculation?.detail?.probs ||
    calculation?.probs ||
    row.probs || { homewin: row.homewin, draw: row.draw, awaywin: row.awaywin };
  const fair = (p) => (numberOrNull(p) > 0 ? 1 / Number(p) : null);
  const outcomes =
    market === "1x2"
      ? ["1", "X", "2"]
      : market === "double-chance" ? ["1X","X2","12"]
      : market === "btts"
      ? ["yes", "no"]
      : market === "asian-handicap"
      ? ["home", "away"]
      : ["over", "under"];
  const resultFair =
    market === "double-chance" ? [fair(Number(resultProbs.homewin)+Number(resultProbs.draw)),fair(Number(resultProbs.draw)+Number(resultProbs.awaywin)),fair(Number(resultProbs.homewin)+Number(resultProbs.awaywin))] :
    market === "1x2"
      ? [
          fair(resultProbs.homewin),
          fair(resultProbs.draw),
          fair(resultProbs.awaywin),
        ]
      : getLeagueFairMarketValues(odds?.prematch_context?.historical ? frozenForecast : calculation?.detail || row, market, {
          goalLine: line,
          handicapLine: line,
          raw: true,
        });
  const statFair =
    market === "double-chance" ? [["homewinprob","drawprob"],["drawprob","awaywinprob"],["homewinprob","awaywinprob"]].map(keys=>{const vals=keys.map(k=>statForecast?.probabilities?.[k]);return vals.every(v=>typeof v==="number")?fair(vals[0]+vals[1]):null;}) :
    market === "1x2"
      ? ["homewinprob", "drawprob", "awaywinprob"].map(
          (k) =>
            statForecast?.fair_odds?.[k] ??
            fair(statForecast?.probabilities?.[k])
        )
      : getLeagueFairMarketValues(statForecast, market, {
          goalLine: line,
          handicapLine: line,
          raw: true,
        });
  const marketValues = outcomes.map(
    (k) =>
      odds?.outcomes?.[k]?.highest ?? null
  );
  const averageValues = outcomes.map((k) => odds?.outcomes?.[k]?.average);
  const indication = workspaceIndications({
    row,
    outcomes,
    models: { result: resultFair, statistic: statFair },
    quotes: { highest: marketValues, average: averageValues },
    threshold: indicationThreshold,
    prematchContext: odds?.prematch_context,
    market, line: ["over-under","asian-handicap"].includes(market)?line:null,
    loading: loading || (!odds && !oddsError),
  });
  const indications = indication.matches;
  const selectedTip = outcomes.includes(selection) ? selection : (indications[0]?.side || outcomes[0]);
  const selectedIndex = outcomes.indexOf(selectedTip);
  const tipName = market === "over-under" ? `${selectedTip === "under" ? "Under" : "Over"} ${String(Number(line)).replace(".",",")} Tore` : market === "asian-handicap" ? `${selectedTip} AH ${line}` : market === "btts" ? `Beide treffen: ${selectedTip === "yes" ? "Ja" : "Nein"}` : selectedTip;
  const tipQuote = marketValues[selectedIndex];
  const tipFairE = resultFair[selectedIndex], tipFairS = statFair[selectedIndex];
  const tipLabel = `Tipp: ${tipName} @${fmt(tipQuote,2)} (fair EBM ${fmt(tipFairE,2)} / SBM ${fmt(tipFairS,2)})`;
  const tipEdge = value => typeof tipQuote === "number" && value > 0 ? `${fmt((tipQuote/value-1)*100,1)} %` : "—";
  const tipIndicated = indications.some(x=>x.side===selectedTip);
  const tipControls = <div className="ef-tip-investigation" aria-label="Untersuchten Tipp auswählen">
    <div className="ef-controls"><label>Markt <select aria-label="Untersuchungsmarkt" value={market} onChange={e=>{setMarket(e.target.value);setSelection("");setLine(e.target.value==="asian-handicap"?"0.00":"2.50");}}><option value="1x2">1X2</option><option value="double-chance">Doppelte Chance</option><option value="over-under">Over/Under</option><option value="asian-handicap">Asian Handicap</option><option value="btts">Beide treffen</option></select></label>
    {["over-under","asian-handicap"].includes(market)&&<label>Linie <input aria-label="Untersuchungslinie" type="number" step="0.25" value={line} onChange={e=>{setLine(e.target.value);setSelection(selectedTip);}} onBlur={()=>{if(line!==""&&Number.isFinite(Number(line)))setLine(Number(line).toFixed(2));}}/></label>}
    <label>Tipp <select aria-label="Untersuchter Tipp" value={selectedTip} onChange={e=>setSelection(e.target.value)}>{outcomes.map(o=><option value={o} key={o}>{o}</option>)}</select></label></div>
    <strong>{tipLabel}</strong><span>{teams.map(x=>x.name).join(" – ")} · Markt Ø {fmt(averageValues[selectedIndex],2)} / Best {fmt(tipQuote,2)} · Vorteil EBM {tipEdge(tipFairE)} / SBM {tipEdge(tipFairS)}</span>
    {comparisonContext&&<small>{comparisonContext}</small>}
    <small>{tipIndicated ? "Indikation beider Modelle" : "Keine aktuelle gemeinsame Indikation – Tipp bleibt untersuchbar"} · Schwelle {indicationThreshold} % · Basis Bestquote · Fokus ist keine Bewertung.</small>
    <div className="ef-tip-goals"><span>Heim / Auswärts / Gesamt: {[ ["EBM",resultGoals],["SBM",statGoals],["Markt",marketGoals]].map(([label,g])=>`${label} ${fmt(g?.home,3)} / ${fmt(g?.away,3)} / ${fmt(typeof g?.home==="number"&&typeof g?.away==="number"?g.home+g.away:null,3)}`).join(" · ")} · Spielstärke-Referenz 1,00</span></div>
  </div>;

  const kickoffDisplay =
    row.details?.date instanceof Date
      ? row.details.date
      : getGermanFixtureDate(row.starting_at);
  const views = buildFixturePerformanceRows(
    performance,
    teams.map((x) => x.id)
  );
  const previous = buildFixturePerformanceRows(
    { actualRows: previousSeasonData?.actualRows || [] },
    teams.map((x) => x.id)
  );
  const close = () => {
    if (busy) return;
    if (hasUnsavedTeams || hasCalculatedDraft || hasFixtureDraftChanges)
      setConfirmClose(true);
    else onClose();
  };
  const act = async (fn, message = "") => {
    setCompletedAction("");
    setBusy(true);
    setLocalError("");
    try {
      const result = await fn();
      if (result === false)
        throw new Error(
          "Aktion fehlgeschlagen. Fehlermeldung prüfen; es wird kein Erfolg bestätigt."
        );
      setCompletedAction(message);
    } catch (e) {
      setLocalError(e.message);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (!hasUnsavedTeams && !hasFixtureDraftChanges) return;
    const guard = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [hasUnsavedTeams, hasFixtureDraftChanges]);
  const pair = (p, digits = 3) =>
    `${fmt(p?.home, digits)} : ${fmt(p?.away, digits)}`;
  return (
    <Dialog
      fullScreen
      open
      onClose={close}
      PaperProps={{ className: "ef-workspace" }}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      aria-labelledby="ef-title"
    >
      <header className="ef-header">
        <div>
          <small>
            {t("Spiel-Arbeitsbereich · Admin", "Match workspace · Admin")}
          </small>
          <Tooltip title={`Fixture-ID: ${row.id}`} arrow>
            <h1 id="ef-title" tabIndex={0}>
              {teams.map((team, i) => (
                <span key={team.side}>
                  {i > 0 && " – "}
                  <Avatar src={team.image_path} alt="" />
                  {team.custom_name || team.name}
                </span>
              ))}
            </h1>
          </Tooltip>
          {actualScore && (
            <p>
              <strong>
                {t("Ergebnis", "Score")}: {pair(actualScore, 0)}
              </strong>{" "}
              ·{" "}
              {t(
                "Abgeschlossen · historische Prognose und heutige Vorschau getrennt",
                "Completed · historical forecast and current preview kept separate"
              )}
            </p>
          )}
          <p>
            {row.league?.name || t("Liga nicht geladen", "League unavailable")}{" "}
            ·{" "}
            {row.season?.name ||
              fixtureContext?.season?.name ||
              t("Saison nicht geladen", "Season unavailable")}{" "}
            ·{" "}
            {row.round?.name || fixtureContext?.round?.name
              ? `${t("Spieltag", "Round")} ${
                  row.round?.name || fixtureContext?.round?.name
                }`
              : t("Spieltag nicht geladen", "Round unavailable")}{" "}
            ·{" "}
            {kickoffDisplay
              ? kickoffDisplay.toLocaleString(en ? "en-GB" : "de-DE", {
                  dateStyle: "medium",
                  timeStyle: "short",
                })
              : "—"}
          </p>
        </div>
        <div>
          <p role="status">
            {busy
              ? t("Aktion läuft …", "Action in progress …")
              : hasCalculatedDraft
              ? t("Vorschau aktuell", "Preview current")
              : hasUnsavedTeams
              ? t(
                  "Offene Änderungen · Vorschau erforderlich",
                  "Pending changes · preview required"
                )
              : t("Gespeicherter Ausgangsstand", "Saved baseline")}
          </p>
          <Button onClick={close} disabled={busy}>
            {t("Zur Spieleauswahl · ", "Back to matches · ")}
            {t("Schließen", "Close")}
          </Button>
        </div>
      </header>
      <DialogContent>
        {tipControls}
        <nav
          className="ef-focus"
          aria-label={t("Analysebereiche", "Analysis sections")}
        >
          {[
            ["market", t("Wettmarkt", "Market")],
            ["goals", t("Torerwartungen", "Expected goals")],
            ["teams", t("Teams & Settings", "Teams & settings")],
            ["league", t("Ligakontext", "League context")],
            ["form", t("Form & Performance", "Form & performance")],
            ["squad", t("Kader", "Squad")],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => {
                const section = document.getElementById("ef-" + id);
                if (section) {
                  section.open = true;
                  section.scrollIntoView({
                    block: "start",
                    behavior: "smooth",
                  });
                }
                if (id === "squad") setSquads(true);
              }}
            >
              {label}
            </button>
          ))}
        </nav>
        {(actionError || localError) && (
          <Alert severity="error">{actionError || localError}</Alert>
        )}
        {loading && (
          <p>
            <CircularProgress size={18} />{" "}
            {t("Statistikmodell wird gelesen …", "Reading statistics model …")}
          </p>
        )}
        {loadError && (
          <Alert severity="info">
            {t(
              "Statistikmodell nicht verfügbar",
              "Statistics model unavailable"
            )}
            : {loadError}
          </Alert>
        )}
        <div className="ef-overview">
          <WorkspaceSection
            id="market"
            title={t("Wettmarkt & Indikation", "Market & indication")}
            open
          >
            <p>Ausgewählt: <strong>{tipLabel}</strong> · Vorteil EBM {tipEdge(tipFairE)} / SBM {tipEdge(tipFairS)}. Auswahl oben ändern.</p>
            {oddsError && (
              <Alert severity="warning">
                {t("Marktquoten nicht geladen", "Market odds unavailable")}:{" "}
                {oddsError}
              </Alert>
            )}
            <div className="ef-scroll">
              <table>
                <thead>
                  <tr>
                    <th>{t("Quelle", "Source")}</th>
                    {outcomes.map((k) => (
                      <th key={k}>{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    [
                      t(
                        "Ergebnisbasiertes Modell · fair",
                        "Results-based model · fair"
                      ),
                      resultFair,
                    ],
                    ...(hasCalculatedDraft && market === "1x2"
                      ? [
                          [
                            t(
                              "Ergebnisbasiertes Modell · gespeichert",
                              "Results-based model · stored"
                            ),
                            [
                              fair(storedProbs.homewin),
                              fair(storedProbs.draw),
                              fair(storedProbs.awaywin),
                            ],
                          ],
                        ]
                      : []),
                    [
                      t(
                        "Statistikbasiertes Modell · fair",
                        "Statistics-based model · fair"
                      ),
                      statFair,
                    ],
                    [
                      odds?.outcomes
                        ? t("Markt · Bestquote", "Market · best odds")
                        : t("Vorhandene Marktquote", "Available market odds"),
                      marketValues,
                    ],
                    [
                      t("Markt · Durchschnitt", "Market · average"),
                      averageValues,
                    ],
                  ].map(([label, values]) => (
                    <tr key={label}>
                      <th>{tr(label)}</th>
                      {outcomes.map((k, i) => (
                        <td
                          className={
                            indications.some(
                              (x) =>
                                x.side === k &&
                                ((values === marketValues &&
                                  x.basis === "highest") ||
                                  (values === averageValues &&
                                    x.basis === "average") ||
                                  (values === resultFair &&
                                    x.model === "result") ||
                                  (values === statFair &&
                                    x.model === "statistic"))
                            )
                              ? "ef-signal"
                              : ""
                          }
                          key={k}
                        >
                          {fmt(values[i], 2)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {odds?.prematch_context?.market_derived_source === "historical_reconstruction" && <small>{t("Marktrückrechnung nachträglich aus historischen Pre-Match-Daten abgeleitet", "Market inversion reconstructed from historical pre-match data")}</small>}
            <small>
              {t(
                "Statistik-Nebenmärkte aus gespeicherter Prognose und eingefrorenen Parametern. Fehlende Werte bleiben leer.",
                "Statistics side markets use the stored forecast and frozen parameters. Missing values remain empty."
              )}
            </small>
          </WorkspaceSection>
          <WorkspaceSection
            id="goals"
            title={t(
              "Torerwartungen · direkter Vergleich",
              "Expected goals · direct comparison"
            )}
            open
          >
            <table aria-label="Torerwartungen im Vergleich">
              <thead>
                <tr>
                  <th>
                    {t(
                      "Quelle / Berechnungsstand",
                      "Source / calculation state"
                    )}
                  </th>
                  <th>{t("Heim", "Home")}</th>
                  <th>{t("Auswärts", "Away")}</th>
                  <th>{t("Gesamt", "Total")}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  [tr("Ergebnisbasiertes Modell · gespeichert"), savedGoals],
                  ...(hasCalculatedDraft
                    ? [[tr("Ergebnisbasiertes Modell · Vorschau"), resultGoals]]
                    : []),
                  [
                    t(
                      "Statistikbasiertes Modell · ",
                      "Statistics-based model · "
                    ) +
                      (!statGoals
                        ? t("nicht verfügbar", "unavailable")
                        : workspace?.selected_preview && !selected
                        ? t("aktuelle Vorschau", "current preview")
                        : statForecast?.kind === "historical_replay"
                        ? t("historische Auswertung", "historical replay")
                        : t("gespeichert", "stored")),
                    statGoals,
                  ],
                  [tr("Markt-Rückrechnung"), marketGoals],
                ].map(([label, goals]) => (
                  <tr key={label}>
                    <th>{tr(label)}</th>
                    <td>{fmt(goals?.home)}</td>
                    <td>{fmt(goals?.away)}</td>
                    <td>{fmt(goalSum(goals))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {goalSum(savedGoals) === null && (
              <Alert severity="info">
                {t(
                  "Keine gespeicherte ergebnisbasierte Prognose für dieses Spiel geladen. Eine Vorschau mit den aktuellen Settings ist separat möglich; sie ersetzt keine historische Prognose.",
                  "No stored results-based forecast loaded for this match. A separate preview with current settings is available; it does not replace a historical forecast."
                )}
              </Alert>
            )}
            {goalSum(marketGoals) === null && (
              <p>
                {t(
                  "Markt-Rückrechnung nicht verfügbar",
                  "Market inversion unavailable"
                )}
                :{" "}
                {{
                  prematch_one_x_two_missing: t(
                    "Keine vollständigen 1X2-Marktquoten vorhanden.",
                    "Complete 1X2 market prices are missing."
                  ),
                  prematch_goal_line_missing: t(
                    "Keine verwertbare Torlinie vorhanden.",
                    "No usable goal line available."
                  ),
                  market_preview_read_failed: t(
                    "Lesender Marktaufruf fehlgeschlagen; erneut laden.",
                    "Market read failed; reload to retry."
                  ),
                }[row.market_preview_reason] ||
                  t(
                    "Marktquoten, Torlinie und gespeicherte Modellbasis prüfen.",
                    "Check market prices, goal line and stored model basis."
                  )}
              </p>
            )}
            {!statGoals && !loadError && (
              <Alert severity="info">
                {workspace?.selected_missing_reason ||
                  loadError ||
                  tr(
                    "Für dieses Spiel liegt keine gespeicherte Statistikprognose vor. Statistikmodell und Saisonstand im Statistikmodell-Bereich prüfen; historische Prognosen werden nicht aus heutigen Settings rekonstruiert."
                  )}
              </Alert>
            )}
            {teams.map((team, i) => {
              const side = i === 0 ? "home" : "away",
                delta = goalDifference(resultGoals, marketGoals)[side];
              return delta == null ? null : (
                <p key={side} className="ef-difference">
                  {t(
                    "Das ergebnisbasierte Modell erwartet für",
                    "The results-based model expects for"
                  )}{" "}
                  <b>{team.name}</b> {fmt(Math.abs(delta))} {t("Tore", "goals")}{" "}
                  {delta >= 0 ? t("mehr", "more") : t("weniger", "fewer")}{" "}
                  {t("als der Markt.", "than the market.")}{" "}
                  <small>
                    {t(
                      "Richtung, keine Qualitätsbewertung.",
                      "Direction, not a quality judgement."
                    )}
                  </small>
                </p>
              );
            })}
            <WorkspaceHelp
              target="fairOdds"
              text={t(
                "Die Rückrechnung schätzt zu den verfügbaren Marktquoten passende Torerwartungen. Sie ist keine eindeutig messbare Teamstärke. Abweichungen zeigen nur mehr oder weniger erwartete Tore.",
                "Inversion estimates expected goals consistent with available market prices. It is not a uniquely measurable team strength. Differences indicate more or fewer expected goals only."
              )}
            >
              {t("Marktquelle und Rückrechnung", "Market source and inversion")}
            </WorkspaceHelp>
            <details>
              <summary>
                {t("Datenstand des Marktes", "Market data state")}
              </summary>
              <p>
                {derived?.provenance?.source ||
                  derived?.source ||
                  tr("Quelle im geladenen Record nicht angegeben")}{" "}
                ·{" "}
                {derived?.provenance?.market_timestamp ||
                  derived?.calculated_at ||
                  derived?.updated_at ||
                  tr("Zeitpunkt nicht angegeben")}
              </p>
              <p>
                {t("Anpassungsfehler", "Fit error")}:{" "}
                {fmt(derived?.market_fit_error)}.{" "}
                {t(
                  "Fehlende Angaben sind nicht als geprüfte hohe Qualität zu verstehen.",
                  "Missing information is not evidence of verified high quality."
                )}
              </p>
            </details>
            {statForecast?.probabilities && (
              <p>
                {t("Statistikbasiertes Modell", "Statistics-based model")} · 1 /
                X / 2:{" "}
                {["homewinprob", "drawprob", "awaywinprob"]
                  .map(
                    (k) =>
                      fmt(
                        numberOrNull(statForecast.probabilities[k]) === null
                          ? null
                          : statForecast.probabilities[k] * 100,
                        1
                      ) + " %"
                  )
                  .join(" / ")}
              </p>
            )}
            {selected?.evaluation && (
              <p>
                {t("Ergebnis", "Score")}{" "}
                {pair(selected.evaluation.actual_goals, 0)} · Should{" "}
                {pair(selected.evaluation.should_goals)}
              </p>
            )}
          </WorkspaceSection>
        </div>
        <WorkspaceSection
          id="interpretation"
          title={t(
            "Einordnung & nächster Prüfschritt",
            "Interpretation & next check"
          )}
        >
          <p>
            {t(
              "Die Unterschiede oben beruhen auf den angezeigten Torerwartungen. Ein positiver Abstand zum Markt allein ist keine Empfehlung: Stichprobe, Datenstand und Gegenargumente zuerst prüfen.",
              "The differences above compare the displayed expected goals. A positive gap from the market alone is not a recommendation: check samples, data freshness and counterarguments first."
            )}
          </p>
          <p>
            {t(
              "Nächster Prüfschritt: Heim-/Auswärtswerte beider Teams vergleichen, dann die letzten Spiele und die Vorsaison prüfen. Eine lokale Änderung zunächst als Vorschau berechnen.",
              "Next check: compare both teams' home/away values, recent games and prior season. Calculate local changes as a preview first."
            )}
          </p>
          {hasUnsavedTeams || hasCalculatedDraft ? (
            <p>
              {t(
                "Offene Änderungen: Eine gespeicherte KI-Einordnung bezieht sich nicht auf diesen Entwurf.",
                "Pending changes: a stored AI explanation does not apply to this draft."
              )}
            </p>
          ) : workspace?.ai_context?.groups ? (
            Object.entries(workspace.ai_context.groups).map(([kind, a]) => (
              <div key={kind}>
                <h3>
                  {kind === "side"
                    ? tr("Mannschaftsrichtung")
                    : tr("Torrichtung")}
                </h3>
                {["stale", "unavailable"].includes(a.status) ? (
                  <p>
                    {a.status === "stale"
                      ? tr(
                          "Die gespeicherte KI-Einordnung ist gegenüber der aktuellen Datengrundlage veraltet."
                        )
                      : tr(
                          "Keine geprüfte gespeicherte KI-Einordnung verfügbar."
                        )}
                  </p>
                ) : (
                  <>
                    <p>
                      {t("KI-Score", "AI score")}: {fmt(a.score, 0)} / ±10 ·{" "}
                      {a.summary || a.reasoning || a.short_reason}
                    </p>
                    <p>
                      {t("Pro-Faktoren", "Supporting factors")}:{" "}
                      {(a.supporting_factors || [])
                        .map((f) => f.text)
                        .join(" · ") || "—"}
                    </p>
                    <p>
                      {t("Gegenargumente", "Counterarguments")}:{" "}
                      {(a.opposing_factors || [])
                        .map((f) => f.text)
                        .join(" · ")}
                    </p>
                    <p>
                      {t("Einschränkungen", "Limitations")}:{" "}
                      {(a.status === "insufficient_data"
                        ? tr("Unzureichende Datenbasis")
                        : null) ||
                        tr(
                          "Die Einordnung gilt nur für die damals übergebenen Daten."
                        )}
                    </p>
                    <small>
                      {a.generated_at} · {a.model}
                    </small>
                  </>
                )}
              </div>
            ))
          ) : (
            <p>
              {workspace?.ai_context?.reason ||
                tr(
                  "Keine gespeicherte KI-Einordnung verfügbar. Die Datenvergleiche und deterministischen Erläuterungen bleiben nutzbar."
                )}
            </p>
          )}
        </WorkspaceSection>
        <WorkspaceSection
          id="teams"
          title={t("Teamvergleich & Settings", "Team comparison & settings")}
          open
        >
          <FixtureTeamComparison row={row} teams={teams} kickoff={kickoff} previous={previous} english={en}
            onContext={setComparisonContext}
            tipContext={{resultGoals,statGoals,marketGoals,statParameters:statForecast?.probability_parameters,resultParameters:frozenForecast?.probability_parameters,selection:selectedTip,label:tipLabel,market,line,outcomes,average:averageValues,highest:marketValues,result:resultFair,statistic:statFair,threshold:indicationThreshold}}
            onOpenLeague={()=>{const el=document.getElementById("ef-league");if(el){el.open=true;el.scrollIntoView({behavior:"smooth",block:"start"});}}}
            onSaved={()=>{setWorkspace(null);onClose();}} />
          <details className="fc-legacy"><summary>Weitere Faktoren & gespeicherte Spieleinschätzung (bisheriger Editor)</summary>
          {teams.map((team, i) => {
            const historical = workspace?.historical_context?.teams?.find(
              (r) => Number(r.team_id) === Number(team.id)
            );
            return (
              <div key={team.side} className="ef-team">
                <h3>{team.custom_name || team.name}</h3>
                {historical?.sources?.length > 1 && (
                  <details>
                    <summary>
                      {t(
                        "Weitere historische Wettbewerbe",
                        "Other historical competitions"
                      )}
                    </summary>
                    {historical.sources.slice(1).map((source) => (
                      <p key={source.season_id}>
                        {source.league_name} · {source.season_name} ·{" "}
                        {source.played} {t("Spiele", "matches")}
                      </p>
                    ))}
                  </details>
                )}
                <details>
                  <summary>
                    {t(
                      "Settings bearbeiten · ergebnisbasiertes Modell",
                      "Edit result settings"
                    )}
                  </summary>
                  <p>
                    {t(
                      "Bearbeitbar: ergebnisbasierte Settings. Alle Felder sind miteinander verknüpft; die vorhandene betty-Rückrechnung aktualisiert abhängige Werte. Statistikbasierte Settings sind hier ausschließlich Vergleichswerte.",
                      "Editable: results-based settings. All fields are linked through the existing betty calculation. Statistics-based settings are read-only comparison values here."
                    )}
                  </p>
                  {[
                    [
                      tr("Spielstärke & Torschnitt"),
                      ["total_strength", "total_goals"],
                      tr(
                        "Spielstärke ist das Verhältnis Tore für / Tore gegen. Der Torschnitt ist deren Summe. Eine Änderung verteilt die Offensive und Defensive über die vorhandene betty-Rückrechnung."
                      ),
                    ],
                    [
                      tr("Offensive & Defensive"),
                      ["for_total", "against_total"],
                      tr(
                        "Mittlere Tore für und gegen das Team; Änderungen werden auf Heim- und Auswärtswerte übertragen. Spielstärke und Torschnitt werden daraus abgeleitet."
                      ),
                    ],
                    [
                      tr("Heim- / Auswärtsdetails"),
                      ["for_home", "against_home", "for_away", "against_away"],
                      tr(
                        "Basiswerte der jeweiligen Spielstätte. Die für diese Paarung relevante Seite ist hervorgehoben. Änderungen aktualisieren Gesamtwerte und Heimfaktoren."
                      ),
                    ],
                    [
                      tr("Weitere Faktoren"),
                      FIXTURE_TEAM_FIELDS.map((x) => x.field).filter(
                        (k) =>
                          ![
                            "total_strength",
                            "total_goals",
                            "for_total",
                            "against_total",
                            "for_home",
                            "against_home",
                            "for_away",
                            "against_away",
                          ].includes(k)
                      ),
                      tr(
                        "Weitere vorhandene Modellfaktoren. Eine Vorschau zeigt ihre gemeinsame Wirkung; ohne explizites Speichern bleiben es lokale Entwürfe."
                      ),
                    ],
                  ].map(([title, keys, help]) => (
                    <fieldset className="ef-edit-group" key={title}>
                      <legend>
                        <WorkspaceHelp text={help}>{title}</WorkspaceHelp>
                      </legend>
                      <div className="ef-edit">
                        {keys.map((field) => (
                          <div key={field}>
                            <WorkspaceHelp text={metricHelp(field)}>
                              {tr(SETTING_LABELS[field] || field)}
                            </WorkspaceHelp>
                            <PreciseSettingInput
                              label={tr(SETTING_LABELS[field] || field)}
                              value={team[field]}
                              disabled={busy}
                              onChange={(value) =>
                                onTeamChange(team.side, field, value)
                              }
                            />
                            {numberOrNull(
                              original.current[team.side][field]
                            ) !== numberOrNull(team[field]) && (
                              <small>
                                {t("Ausgang", "Baseline")}:{" "}
                                {fmt(original.current[team.side][field])} →{" "}
                                {fmt(team[field])}
                              </small>
                            )}
                          </div>
                        ))}
                      </div>
                    </fieldset>
                  ))}
                </details>
              </div>
            );
          })}
          <div className="ef-actions" role="status">
            <small>
              {t(
                "Vorschau berechnet Torerwartungen und faire Quoten mit dem lokalen Entwurf, ohne dauerhafte Speicherung. Bei weiterer Bearbeitung wird die Vorschau verworfen.",
                "Preview calculates expected goals and fair odds using the local draft without saving. Further edits invalidate the preview."
              )}
            </small>
            {hasUnsavedTeams &&
              teams
                .filter(
                  (team) =>
                    JSON.stringify(original.current[team.side]) !==
                    JSON.stringify(team.side === "home" ? homeDraft : awayDraft)
                )
                .map((team) => (
                  <span key={team.side}>
                    {team.name} ·{" "}
                    {t(
                      "ergebnisbasiertes Modell geändert",
                      "results-based model changed"
                    )}
                  </span>
                ))}
            {busy && (
              <span>
                {t(
                  "Berechnung / Aktion läuft …",
                  "Calculation / action in progress …"
                )}
              </span>
            )}
            {completedAction && !hasUnsavedTeams && (
              <span>{completedAction}</span>
            )}
            <b>
              {hasUnsavedTeams
                ? t(
                    "Settings geändert · noch nicht gespeichert",
                    "Settings changed · not saved"
                  )
                : t(
                    "Gespeicherter Ausgangsstand · Vorschau ohne Speicherung möglich",
                    "No pending setting changes"
                  )}
            </b>
            {hasCalculatedDraft && (
              <span>
                {t(
                  "Vorschau aktuell · Ergebnisse oben im Modellvergleich",
                  "Calculated draft"
                )}
              </span>
            )}
            {fixtureEstimateSaved && (
              <span>
                {t("Spieleinschätzung gespeichert", "Fixture estimate saved")}
              </span>
            )}
            <Button disabled={busy} onClick={() => act(onRecompile)}>
              {t("Vorschau berechnen", "Preview / recalculate")}
            </Button>
            <Button
              disabled={busy || !hasUnsavedTeams}
              onClick={() =>
                act(
                  onSaveTeams,
                  t(
                    "Team Settings gespeichert und vom Server erneut gelesen.",
                    "Team settings saved and verified by server read-back."
                  )
                )
              }
            >
              {t("Team Settings dauerhaft speichern", "Save team settings")}
            </Button>
            <Button
              disabled={
                busy || !hasCalculatedDraft || Date.parse(kickoff) <= Date.now()
              }
              title={t(
                "Speichert die Berechnung nur für ein noch nicht begonnenes Spiel. Historische Prognosen bleiben geschützt.",
                "Saves the calculation for a match that has not started. Historical forecasts remain protected."
              )}
              onClick={() => act(onSaveFixtureEstimate)}
            >
              {t("Spieleinschätzung speichern", "Save fixture estimate")}
            </Button>
            <Button disabled={busy} onClick={() => act(onReset)}>
              {t("Änderungen verwerfen", "Reset / original values")}
            </Button>
          </div>
          </details>
        </WorkspaceSection>
        <WorkspaceSection
          id="league"
          title={t(
            "Ligakontext · Tabelle & Ranking",
            "League context · table & ranking"
          )}
        >
          <p>
            {t("Gespeicherter Torschnitt", "Stored goal average")}:{" "}
            {fmt(seasonParameters?.stored_goal_average)} ·{" "}
            {t("Heimvorteil", "Home advantage")}:{" "}
            {fmt(seasonParameters?.exp_home_factor)} ·{" "}
            {t("Remisfaktor", "Draw factor")}:{" "}
            {fmt(seasonParameters?.drawfactor)}
          </p>
          <div className="ef-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t("Saisonvergleich", "Season comparison")}</th>
                  <th>{t("Spiele", "Games")}</th>
                  <th>{t("Torschnitt", "Goals per game")}</th>
                  <th>{t("Heimvorteil", "Home advantage")}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Real", "actualRows", "actual"],
                  ["Expected", "expectedRows", "expected"],
                  [t("Markt", "Market"), "marketRows", "market"],
                ].map(([label, key, view]) => {
                  const context = leagueGoalContext(performance[key], view);
                  return (
                    <tr key={key}>
                      <th>{tr(label)}</th>
                      <td>{context.games}</td>
                      <td>{fmt(context.average)}</td>
                      <td>{fmt(context.homeFactor)}</td>
                    </tr>
                  );
                })}
                <tr>
                  <th>
                    <WorkspaceHelp
                      target="shouldTable"
                      text={t(
                        "Should beschreibt die aus tatsächlichen Spielstatistiken abgeleiteten Tore. Hier mit vor dem Spiel eingefrorener individueller Conversion; keine nachträgliche Prognose.",
                        "Should goals are derived from observed match statistics using the individual conversion frozen before kickoff, not a retrospective forecast."
                      )}
                    >
                      Should
                    </WorkspaceHelp>
                  </th>
                  <td>{adaptiveLeagueContext(observations).games}</td>
                  <td>{fmt(adaptiveLeagueContext(observations).average)}</td>
                  <td>{fmt(adaptiveLeagueContext(observations).homeFactor)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <WorkspaceTableAndRanking
            performance={{ ...performance, shouldRows: [] }}
            teamIds={teams.map((x) => x.id)}
            strengthRows={strengthRanking}
            workspacePresentation={{
              locale: en ? "en-GB" : "de-DE",
              translate: t,
            }}
          />
          <small>
            {t(
              "Adaptive Should-Werte ausschließlich im Team-/Formvergleich; keine künstlichen Should-Punkte.",
              "Adaptive Should values in team/form comparison only; no invented Should points."
            )}
          </small>
        </WorkspaceSection>
        <WorkspaceSection
          id="form"
          title={t(
            "Letzte Spiele & Performance",
            "Recent matches & performance"
          )}
        >
          {formContent && (
            <div style={{ display: "none" }}>
              {cloneElement(formContent, {
                autoLoadRecentGames: true,
                onRecentGamesLoaded: setRecentGames,
              })}
            </div>
          )}
          <small>
            {t(
              "Bis zu acht Spiele vor dieser Paarung. Fehlende Modellwerte bleiben leer; Should stammt ausschließlich aus adaptiven Records.",
              "Up to eight matches before this fixture. Missing model values stay empty; Should uses adaptive records only."
            )}
          </small>
          {!observations.length && (
            <p>
              {t(
                "Keine gespeicherten abgeschlossenen Statistikmodell-Spiele vor diesem Fixture vorhanden.",
                "No stored completed statistics-model fixtures before this match."
              )}
            </p>
          )}
          {teams.map((team) => {
            const rows = consolidatedRecentRows(
              observations,
              workspace?.comparison?.rows,
              team.id,
              recentGames?.[team.side],
              performance.expectedLedger,
              kickoff
            );
            const diffs = rows.filter(
              (r) => goalSum(r.actual) !== null && goalSum(r.expected) !== null
            );
            return (
              <div key={team.side}>
                <h3>
                  {team.custom_name || team.name} · {rows.length}{" "}
                  {t("Spiele", "matches")}
                </h3>
                <div className="ef-scroll">
                  <table>
                    <thead>
                      <tr>
                        {[
                          t("Datum", "Date"),
                          t("Ort", "Venue"),
                          t("Gegner", "Opponent"),
                          t("Ergebnis H/A", "Score H/A"),
                          "Expected H/A",
                          "Should H/A",
                          t(
                            "Statistikmodell · Tore H/A",
                            "Statistics model · goals H/A"
                          ),
                          "Performance",
                        ].map((x) => (
                          <th key={x}>{x}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id}>
                          <td>{r.date?.slice(0, 10)}</td>
                          <td>{r.home ? "H" : "A"}</td>
                          <th>{r.opponent}</th>
                          <td>{pair(r.actual, 0)}</td>
                          <td>{pair(r.expected)}</td>
                          <td>{pair(r.should)}</td>
                          <td>{pair(r.statistics)}</td>
                          <td>{fmt(r.performance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p>
                  {["actual", "expected", "should"].map((k) => {
                    const s = summarizeRecent(rows, k);
                    return (
                      <span key={k}>
                        {
                          {
                            actual: t("Summe Tore", "Total goals"),
                            expected: t("Summe Expected", "Total Expected"),
                            should: t("Summe Should", "Total Should"),
                          }[k]
                        }
                        : {fmt(s.for, k === "actual" ? 0 : 3)} :{" "}
                        {fmt(s.against, k === "actual" ? 0 : 3)} ({s.count}/
                        {rows.length}) ·{" "}
                      </span>
                    );
                  })}
                </p>
                <small>
                  {t(
                    "Ø Torabweichung Actual − Expected",
                    "Mean goal deviation Actual − Expected"
                  )}
                  :{" "}
                  {fmt(
                    diffs.length
                      ? diffs.reduce(
                          (n, r) => n + goalSum(r.actual) - goalSum(r.expected),
                          0
                        ) / diffs.length
                      : null
                  )}{" "}
                  · Performance = (Should-Differenz − Expected-Differenz) +
                  (Real-Differenz − Expected-Differenz) / 3
                </small>
              </div>
            );
          })}
        </WorkspaceSection>
        <details
          id="ef-squad"
          className="ef-section"
          onToggle={(e) => setSquads(e.currentTarget.open)}
        >
          <summary>
            {t("Kader / Spieler / Aufstellung", "Squad / players / lineup")}
          </summary>
          <div className="ef-section-body">
            <FixtureSquads
              open={squads}
              seasonId={row.season_id}
              fixtureId={row.id}
              teams={teams}
            />
          </div>
        </details>
        {comparison && (
          <WorkspaceSection
            id="provenance"
            title={t("Datenstand & Modellherkunft", "Data provenance")}
          >
            <p>
              {t("Ergebnisprognose", "Result forecast")}:{" "}
              {comparison.result_model?.calculated_at || "—"} ·{" "}
              {comparison.result_model?.source || "—"}
            </p>
            <p>
              {t("Statistikprognose", "Statistics forecast")}:{" "}
              {statForecast?.calculated_at || "—"} ·{" "}
              {statForecast?.model_version || "—"} · {statForecast?.kind || "—"}
            </p>
            <p>
              {t("Erwartete Angriffsmomente", "Expected attack moments")}:{" "}
              {pair(statForecast?.expected_attack_moments)} ·{" "}
              {t("Erwartete Conversion", "Expected conversion")}:{" "}
              {fmt(
                numberOrNull(statForecast?.expected_conversion?.home) === null
                  ? null
                  : statForecast.expected_conversion.home * 100
              )}{" "}
              % :{" "}
              {fmt(
                numberOrNull(statForecast?.expected_conversion?.away) === null
                  ? null
                  : statForecast.expected_conversion.away * 100
              )}{" "}
              %
            </p>
          </WorkspaceSection>
        )}
      </DialogContent>
      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogContent>
          <p>
            {t(
              "Der lokale Entwurf bleibt beim Schließen erhalten. Ohne Speichern zurückkehren?",
              "The local draft is retained when closing. Return without saving?"
            )}
          </p>
          <Button onClick={() => setConfirmClose(false)}>
            {t("Weiter bearbeiten", "Continue editing")}
          </Button>
          <Button onClick={onClose}>
            {t("Ohne Speichern schließen", "Close without saving")}
          </Button>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
